import { ChevronLeft, ChevronRight, Phone, VolumeX } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { OFFER_PROXIMITY, offerProximity, type OfferProximity } from '@despachador/domain';
import { useNow, Modal, Field } from '@despachador/ui';
import { useDriver } from './store';

/**
 * Pick-up and destination, always in this order and with the same marks as the operator's map
 * (solid square = pick-up, hollow = destination). `focus` enlarges the point the driver is heading to.
 */
export function RoutePoints({ origin, destination, focus }: { origin: ReactNode; destination: ReactNode; focus?: 'origin' | 'destination' }) {
  return (
    <ol className="route-points">
      <li className={focus === 'origin' ? 'focus' : undefined}>
        <span className="mark" aria-hidden />
        <span className="route-label">Recoger en</span>
        <span className="route-place">{origin}</span>
      </li>
      <li className={focus === 'destination' ? 'focus' : undefined}>
        <span className="mark hollow" aria-hidden />
        <span className="route-label">Destino</span>
        <span className="route-place">{destination}</span>
      </li>
    </ol>
  );
}

/**
 * A screen one level below a tab: the way back and its title, always in the same place, on a surface
 * header (DRV-010). `aside` goes next to the title (a state badge); `intro` under it. The body is
 * padded unless `flush`: then its groups run edge to edge, separated by lines.
 */
export function SubPage({
  title,
  back = '/cuenta',
  backLabel = 'Cuenta',
  aside,
  intro,
  flush,
  children,
}: {
  title: string;
  back?: string;
  backLabel?: string;
  aside?: ReactNode;
  intro?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <header className="sub-head">
        <Link to={back} className="back-link">
          <ChevronLeft size={18} aria-hidden /> {backLabel}
        </Link>
        <div className="sub-title-row">
          <h1 className="page-title">{title}</h1>
          {aside}
        </div>
        {intro && <p className="sub-intro">{intro}</p>}
      </header>
      <div className={flush ? 'sub-body flush' : 'sub-body page-pad'}>{children}</div>
    </>
  );
}

/** A group of rows that open something: a micro title, then icon, name and, on the right, the current value or state. */
export function MenuList({ label, items }: { label: string; items: Array<{ to: string; icon: ReactNode; name: string; value?: ReactNode }> }) {
  return (
    <section className="menu-group">
      <h2 className="micro-title">{label}</h2>
      <nav className="menu-list" aria-label={label}>
        {items.map((i) => (
          <Link key={i.to} to={i.to}>
            {i.icon}
            <span className="menu-name">{i.name}</span>
            {i.value !== undefined && <span className="menu-value">{i.value}</span>}
            <ChevronRight size={18} aria-hidden className="chev" />
          </Link>
        ))}
      </nav>
    </section>
  );
}

/**
 * One choice among a few (DRV-010): a radio group of buttons. Arrows move the choice, as in any native
 * radio group; only the chosen one is in the tab order. `variant`: «segmented» (one row, equal parts:
 * the theme, the alert tone) or «chips» (wrap to a second line when they do not fit: filters).
 */
export function ChoiceGroup<T extends string>({
  options,
  value,
  onChange,
  label,
  labelledBy,
  describedBy,
  variant = 'chips',
  disabled,
}: {
  options: Array<{ value: T; label: ReactNode; count?: number }>;
  value: T;
  onChange: (v: T) => void;
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  variant?: 'segmented' | 'chips';
  disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const n = (i + step + options.length) % options.length;
    onChange(options[n]!.value);
    refs.current[n]?.focus();
  };
  return (
    <div className={variant === 'segmented' ? 'choice segmented' : 'choice chips'} role="radiogroup" aria-label={label} aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {o.label}
            {o.count !== undefined && <span className="count">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The traffic light of an offer (DRV-009): the color always comes with its word. */
const PROXIMITY: Record<OfferProximity, string> = { near: 'Cerca', mid: 'Media', far: 'Lejos' };

/** "$ 10.400" -> ["$", "10.400"]: the symbol goes smaller, the amount is what the driver reads. */
function splitFare(text: string | null): [string, string] {
  if (!text) return ['', '—'];
  const m = text.match(/^([^\d-]*?)\s*(-?\d.*)$/);
  return m ? [m[1]!.trim(), m[2]!] : ['', text];
}

/** Metres as the value of the data strip; the unit goes apart ("2.6" + "km"). */
const kmValue = (m: number) => (m / 1000).toFixed(1);

/** Last seconds of an offer: the bar and the counter turn to the accent. */
const URGENT_S = 10;

/** A short buzz on the answer, where the phone lets a page vibrate. */
function buzz() {
  try {
    navigator.vibrate?.(30);
  } catch {
    // some browsers refuse it outside a user gesture: the answer goes on anyway
  }
}

/**
 * New service offer (DRV-009 redesign): one dark, high-contrast takeover, read at a glance in 20 s.
 * The time left comes from the server's deadline (`expiresAt`), never from a local counter.
 */
export function OfferOverlay() {
  const { offer, respond, alertFallback, enableAudio } = useDriver();
  const now = useNow(250);
  const [busy, setBusy] = useState(false);
  // a far pickup is accepted with a second tap, for this offer only
  const [confirmFor, setConfirmFor] = useState<string | null>(null);
  if (!offer) return null;
  const total = Math.max(1, offer.timeoutS);
  const leftS = Math.max(0, (new Date(offer.expiresAt).getTime() - now) / 1000);
  const left = Math.ceil(leftS);
  const proximity = offerProximity(offer.etaS, offer.distanceM);
  // without a route time, the same estimate the traffic light uses
  const etaS = offer.etaS ?? (offer.distanceM !== null ? offer.distanceM / OFFER_PROXIMITY.fallbackSpeedMps : null);
  const etaMin = etaS === null ? null : Math.max(1, Math.round(etaS / 60));
  const confirming = confirmFor === offer.offerId;
  const [currency, amount] = splitFare(offer.fareText);
  const origin = offer.originText ?? `${offer.origin.lat.toFixed(4)}, ${offer.origin.lng.toFixed(4)}`;
  const act = async (accept: boolean) => {
    buzz();
    if (accept && proximity === 'far' && !confirming) {
      setConfirmFor(offer.offerId);
      return;
    }
    setBusy(true);
    await respond(accept);
    setBusy(false);
  };
  return (
    <div className={`offer${left <= URGENT_S ? ' urgent' : ''}`} role="alertdialog" aria-modal="true" aria-label="Nuevo servicio" aria-describedby="offer-summary">
      <div className="offer-sheet">
        <div className="offer-bar" aria-hidden>
          <span style={{ transform: `scaleX(${Math.min(1, leftS / total).toFixed(4)})` }} />
        </div>
        {alertFallback && (
          <button className="sound-banner" onClick={() => void enableAudio()}>
            <VolumeX size={18} aria-hidden /> Toca aquí para activar el sonido de alertas
          </button>
        )}
        <header className="offer-head">
          <span className="offer-new">
            <span className="ping" aria-hidden />
            Nuevo servicio
          </span>
          <span className="offer-code">{offer.code}</span>
        </header>

        <section className="offer-top" aria-label="Tarifa y tiempo">
          <div className="offer-fare">
            <span className="offer-label">Tarifa estimada</span>
            <p className="fare" id="offer-summary">
              {currency && <span className="cur">{currency}</span>}
              {amount}
              <span className="sr-only">
                . {proximity ? `${PROXIMITY[proximity]}: ` : ''}llegas en {etaMin ?? '—'} minutos; recoger en {origin}.
              </span>
            </p>
          </div>
          <div className="offer-timer" role="timer" aria-label={`${left} segundos para responder`}>
            <span className="secs">{left}</span>
            <span className="unit">seg</span>
          </div>
        </section>

        <dl className="offer-facts">
          <div className={`fact eta ${proximity ?? 'unknown'}`}>
            <dt>
              <span className="sq" aria-hidden />
              {proximity ? PROXIMITY[proximity] : 'Llegada'}
            </dt>
            <dd>
              {etaMin ?? '—'} <small>min</small>
            </dd>
          </div>
          <div className="fact">
            <dt>A recogida</dt>
            <dd>{offer.distanceM !== null ? <>{kmValue(offer.distanceM)} <small>km</small></> : '—'}</dd>
          </div>
          <div className="fact">
            <dt>Viaje</dt>
            {offer.tripDistanceM !== null ? (
              <dd>
                {kmValue(offer.tripDistanceM)} <small>km</small>
              </dd>
            ) : (
              <dd className="pending">
                <small>Por confirmar</small>
              </dd>
            )}
          </div>
        </dl>

        <section className="offer-route" aria-label="Ruta">
          <ol>
            <li className="pickup">
              <span className="mark" aria-hidden />
              <span className="offer-label">Recoger en</span>
              <p>{origin}</p>
            </li>
            <li className="dropoff">
              <span className="mark" aria-hidden />
              <span className="offer-label">Destino</span>
              <p>{offer.destinationText ?? 'Lo indica el cliente'}</p>
            </li>
          </ol>
        </section>

        {confirming && (
          <p className="offer-confirm" role="status">
            Está a {etaMin} min de ti. ¿Aceptas igual?
          </p>
        )}
        <div className="offer-actions">
          <button type="button" className="reject" disabled={busy} onClick={() => void act(false)}>
            Rechazar
          </button>
          <button type="button" className="accept" disabled={busy} onClick={() => void act(true)} autoFocus>
            <span>{busy ? 'Enviando…' : confirming ? 'Sí, acepto' : 'Aceptar'}</span>
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** The offer ran out of time unanswered (DRV-009): said once, full screen; the card stays on the home screen. */
export function OfferExpired() {
  const { offer, result } = useDriver();
  const [seen, setSeen] = useState<object | null>(null);
  if (offer || !result?.timedOut || seen === result) return null;
  return (
    <div className="offer offer-done" role="status" aria-labelledby="offer-done-title">
      <div className="offer-sheet">
        <div className="done-body">
          <span className="sq" aria-hidden />
          <h1 id="offer-done-title">Se acabó el tiempo</h1>
          <p>No respondiste a tiempo y el servicio pasó a otro conductor.</p>
        </div>
        <button
          type="button"
          className="done-cta"
          autoFocus
          onClick={() => setSeen(result)}
        >
          Volver
        </button>
      </div>
    </div>
  );
}

export function CallOverlay() {
  const { call, acceptCall, endCall } = useDriver();
  if (!call) return null;
  return (
    <div className="call" role="alertdialog" aria-modal="true" aria-label={call.state === 'ringing' ? 'Llamada entrante' : 'Llamada en curso'}>
      <div className="avatar"><Phone size={40} aria-hidden /></div>
      <h2>{call.from?.name ?? 'Operación'}</h2>
      <div>{call.state === 'ringing' ? 'Llamada entrante…' : 'En llamada'}</div>
      <div className="row">
        {call.state === 'ringing' ? (
          <>
            <button type="button" className="btn danger lg" onClick={() => void endCall(true)}>
              Rechazar
            </button>
            <button type="button" className="btn ok lg" onClick={() => void acceptCall()} autoFocus>
              Contestar
            </button>
          </>
        ) : (
          <button type="button" className="btn danger lg" onClick={() => void endCall(false)} autoFocus>
            Colgar
          </button>
        )}
      </div>
    </div>
  );
}

export function ReasonModal({
  title,
  onClose,
  onSubmit,
  options,
  confirmLabel = 'Confirmar',
  busy,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  options: string[];
  confirmLabel?: string;
  busy?: boolean;
}) {
  // nothing preselected: the reason is audited, it must be a real choice
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const text = reason === '__other' ? other.trim() : reason;
  const valid = reason !== '' && (reason !== '__other' || text.length >= 3);
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn lg" onClick={onClose}>
            Volver
          </button>
          <button type="button" className="btn danger lg" disabled={!valid || busy} onClick={() => onSubmit(text)}>
            {busy ? 'Enviando…' : confirmLabel}
          </button>
        </>
      }
    >
      <fieldset className="reason-list">
        <legend className="sr-only">Motivo</legend>
        {options.map((o) => (
          <label key={o} className="check reason">
            <input type="radio" name="reason" checked={reason === o} onChange={() => setReason(o)} /> {o}
          </label>
        ))}
        <label className="check reason">
          <input type="radio" name="reason" checked={reason === '__other'} onChange={() => setReason('__other')} /> Otro motivo
        </label>
      </fieldset>
      {reason === '__other' && (
        <Field label="Describe el motivo" hint="Mínimo 3 caracteres.">
          <input className="input" value={other} onChange={(e) => setOther(e.target.value)} maxLength={200} data-autofocus="" />
        </Field>
      )}
    </Modal>
  );
}
